import React, { useState } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "../../components/Select";
import Spinner from "../../components/Spinner";
import useAuthFetch from "../../hooks/useAuthFetch";

const SWAL_THEME = {
  background: "#76236C",
  color: "#fafafa",
  confirmButtonColor: "#19D3C5",
};

const ROLE_OPTIONS = [
  { label: "Dependiente", value: "dependiente" },
  { label: "Bodega", value: "bodega" },
  { label: "Mensajero", value: "mensajero" },
  { label: "Administrador de tienda", value: "administradorTienda" },
  { label: "Administrador de inventario", value: "administradorInventario" },
  { label: "Integración sistema", value: "integracionSistema" },
  { label: "Administrador", value: "admin" },
];

const ColaboradoresForm = () => {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [loading, setLoading] = useState(false);
  const [nombre, setNombre] = useState("");
  const [apellido1, setApellido1] = useState("");
  const [email, setEmail] = useState("");
  const [codigoPaisTelefono, setCodigoPaisTelefono] = useState("506");
  const [telefono, setTelefono] = useState("");
  const [roles, setRoles] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [error, setError] = useState(false);
  const [msgError, setMsgError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);

    if (!nombre.trim() || !apellido1.trim()) {
      setMsgError("Nombre y apellido son obligatorios");
      setError(true);
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      setMsgError("Ingrese un correo válido");
      setError(true);
      return;
    }

    if (!roles) {
      setMsgError("Seleccione un rol");
      setError(true);
      return;
    }

    if (!password || password.length < 6) {
      setMsgError("La contraseña debe tener al menos 6 caracteres");
      setError(true);
      return;
    }

    if (password !== passwordConfirmation) {
      setMsgError("Las contraseñas no coinciden");
      setError(true);
      return;
    }

    setLoading(true);
    const res = await authFetch(
      "usuarios/colaboradores",
      {
        nombre: nombre.trim(),
        apellido1: apellido1.trim(),
        email: email.trim(),
        codigoPaisTelefono: parseInt(codigoPaisTelefono, 10) || 506,
        telefono: telefono ? parseInt(telefono, 10) : undefined,
        roles,
        password,
      },
      "POST"
    );

    if (res.status === 200 || res.status === 201) {
      Swal.fire({
        icon: "success",
        title: "Correcto",
        text: "Colaborador creado correctamente",
        ...SWAL_THEME,
      });
      setTimeout(() => navigate("/colaboradores"), 400);
      return;
    }

    setLoading(false);
    let message = "Ocurrió un error al crear el colaborador";
    try {
      const body = await res.json();
      if (body?.error?.message) {
        message = body.error.message;
      }
    } catch (_) {
      // ponytail: ignore parse errors
    }
    if (res.status === 409) {
      message = "Ya existe una cuenta con este correo";
    }
    setMsgError(message);
    setError(true);
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="w-full p-content pb-16">
        <div className="flex w-full items-center">
          <div className="w-full">
            <Link to="/colaboradores">
              <button
                type="button"
                className="bg-pink text-white py-2 px-8 font-bold rounded-full float-right flex justify-center items-center"
              >
                <IoMdArrowRoundBack className="text-lg" /> Volver
              </button>
            </Link>
          </div>
        </div>

        <h2 className="my-3 font-bold text-baseGray text-2xl">
          Nuevo colaborador
        </h2>

        <form className="mt-5" onSubmit={handleSubmit}>
          <div className="my-2">
            <label className="ml-5">Nombre</label>
            <input
              className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              type="text"
              name="nombre"
            />
          </div>

          <div className="my-2">
            <label className="ml-5">Apellido</label>
            <input
              className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
              value={apellido1}
              onChange={(e) => setApellido1(e.target.value)}
              type="text"
              name="apellido1"
            />
          </div>

          <div className="my-2">
            <label className="ml-5">Correo electrónico</label>
            <input
              className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              name="email"
            />
          </div>

          <div className="my-2 flex gap-4">
            <div className="w-1/3">
              <label className="ml-5">Código país</label>
              <input
                className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
                value={codigoPaisTelefono}
                onChange={(e) => setCodigoPaisTelefono(e.target.value)}
                type="text"
                name="codigoPaisTelefono"
              />
            </div>
            <div className="w-2/3">
              <label className="ml-5">Teléfono</label>
              <input
                className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                type="text"
                name="telefono"
              />
            </div>
          </div>

          <div className="my-4">
            <Select
              label="Rol"
              labelColor="text-baseGray"
              options={ROLE_OPTIONS}
              value={roles}
              onChange={(value) => setRoles(value)}
              required
            />
          </div>

          <div className="my-2">
            <label className="ml-5">Contraseña inicial</label>
            <input
              className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              name="password"
            />
          </div>

          <div className="my-2">
            <label className="ml-5">Confirmar contraseña</label>
            <input
              className="w-full rounded-3xl my-2 h-12 p-6 ring-1 ring-purple outline-none"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              type="password"
              name="passwordConfirmation"
            />
          </div>

          {error && (
            <p className="text-pink font-medium my-4 ml-5">{msgError}</p>
          )}

          <button
            type="submit"
            className="bg-cyan text-white px-8 py-3 font-bold rounded-full mt-4"
            disabled={loading}
          >
            Crear colaborador
          </button>
        </form>
      </div>
    </>
  );
};

export default ColaboradoresForm;
